import Dragger from 'antd/lib/upload/Dragger'
import React, { useState } from 'react'
import csvToJSON from '../../../common/csv-to-json'
import { useLocation } from 'wouter'
import { Button, Table, message, InputNumber, Switch, Input } from 'antd'
import { InboxOutlined } from '@ant-design/icons'
import { Loading } from '@guillotinaweb/react-gmi'
import { useMutation, useQuery } from 'urql'
import { ContentLayout } from '../../../common/content-layout'
import { CreateOrderInvoice } from './graphql'
import { ProductSelector } from '../../../components/product-selector'

import './styles.css'

const SearchProductQuery = `
  query($search: String) {
    pedido_search_producto(query: $search) {
      precio
      id_producto
      disponibilidad
      nombre
      visibilidad
    }
  }
`

const mandatoryAddressColumns = [
  'linea1',
  'codigo_postal',
  'telefono',
  'destinatario',
  'id_provincia',
  'id_pais',
  'ciudad',
]
const supportedAddressColumns = [...mandatoryAddressColumns, 'linea2', 'linea3']

export function AddOrder({ id }) {
  const [, createOrderMutation] = useMutation(CreateOrderInvoice)
  const [, setLocation] = useLocation()

  // Product state
  const [lines, setLines] = useState([{ add: true }])
  const [product, setProduct] = useState()
  const [quantity, setQuantity] = useState(1)
  const [price, setPrice] = useState()
  const [discount, setDiscount] = useState(0)
  const productLines = lines.filter((l) => !l.add)
  const [productSearch, setProductSearch] = useState('')
  const [suggestions] = useQuery({
    query: SearchProductQuery,
    variables: { search: productSearch },
    pause: productSearch.length === 0,
  })

  // Order satte
  const [gift, setGift] = useState(false)
  const [presentMessage, setPresentMessage] = useState('')

  // Address state
  const [CSVs, setCSVs] = useState({})
  const [reading, setReading] = useState(false)
  const address = Object.values(CSVs).flat()

  function add() {
    const row = { product, quantity, price, discount }
    if (Object.values(row).some((v) => v === undefined))
      return message.error('You should fill all fields')
    setLines((l) => [row, ...l])
    setProduct()
    setQuantity(1)
    setPrice()
    setDiscount(0)
    message.success('Product added')
  }

  function remove(i) {
    setLines((l) => l.filter((v, index) => index !== i))
  }

  async function createOrder() {
    const products = productLines.map((l) => ({
      id_producto: l.product.ref,
      quantity: l.quantity,
      price: l.price - (l.price * l.discount) / 100,
    }))
    const query = { id, products, address, gift_paper: gift }

    if (products.length === 0) {
      message.error('You forgot to add products')
      return
    }
    if (address.length === 0) {
      message.error('You forgot to add addresses')
      return
    }
    if (presentMessage) query.present_message = presentMessage

    const res = await createOrderMutation(query)
    if (res.error) return message.error(res.error.message)
    message.success('Order confirmed correctly')
    setLocation(`/orders/multi-order-invoice/${id}?refresh=1`)
  }

  function onLoadCSV({ file }) {
    if (CSVs[file.uid] || file.status !== 'done') return
    setReading(true)
    const reader = new FileReader()

    reader.addEventListener('load', async (e) => {
      const data = csvToJSON(e.target.result)
      const [columns] = data

      if (mandatoryAddressColumns.some((k) => !columns[k])) {
        message.error(
          `You are missing some mandatory address column: ${mandatoryAddressColumns.join(
            ', '
          )}`
        )
        setReading(false)
        return
      }

      // Ignore columns that don't match with supported columns
      const cleanData = data.map((o) =>
        supportedAddressColumns.reduce((t, c) => {
          t[c] = o[c]
          return t
        }, {})
      )

      await setCSVs((c) => ({ ...c, [file.uid]: cleanData }))
      setReading(false)
    })
    reader.readAsText(file.originFileObj)
  }

  return (
    <ContentLayout
      displayBackBtn
      title={`New order for invoice #${id}`}
      buttonLabel="Confirm new order"
      buttonAction={createOrder}
    >
      <div style={{ padding: 20 }}>
        <h2 className="title is-size-6">Products</h2>
        <Table
          dataSource={lines}
          pagination={false}
          columns={[
            {
              title: 'Quantity',
              dataIndex: 'quantity',
              width: 30,
              key: 'quantity',
              render: (v, o) =>
                o.add ? (
                  <InputNumber
                    value={quantity}
                    onChange={setQuantity}
                    min={1}
                  />
                ) : (
                  v
                ),
            },
            {
              title: 'Product',
              key: 'product',
              width: 400,
              dataIndex: 'product',
              render: (v, o) =>
                o.add ? (
                  <ProductSelector
                    placeholder=" "
                    prodSugggestions={suggestions?.data?.pedido_search_producto?.map(
                      (p) => ({
                        id: p.id_producto,
                        ref: p.id_producto,
                        nombre: p.nombre,
                        option: { precio: p.precio },
                      })
                    )}
                    onSearch={setProductSearch}
                    disabledIds={
                      new Set(productLines.map((l) => l.product.ref))
                    }
                    products={product ? [product.ref] : []}
                    type="refs"
                    onChange={(prods, suggestions) => {
                      const productId = prods[prods.length - 1]
                      const prod = suggestions.find((p) => p.ref === productId)
                      if (!prod) return
                      const price = prod.option.precio
                      setProduct(prod)
                      if (price) setPrice(price)
                    }}
                  />
                ) : (
                  `${v.nombre} (${v.ref})`
                ),
            },
            {
              title: 'Price (€)',
              dataIndex: 'price',
              width: 30,
              suffix: '€',
              key: 'price',
              render: (v, o) =>
                o.add ? (
                  <InputNumber value={price} onChange={setPrice} min={0} />
                ) : (
                  v
                ),
            },
            {
              title: 'Discount (%)',
              dataIndex: 'discount',
              width: 30,
              key: 'discount',
              render: (v, o) =>
                o.add ? (
                  <InputNumber
                    value={discount}
                    onChange={setDiscount}
                    min={0}
                    max={100}
                  />
                ) : (
                  v
                ),
            },
            {
              title: '',
              dataIndex: '',
              key: '',
              render: (v, o, i) => (
                <Button onClick={() => (o.add ? add() : remove(i))}>
                  {o.add ? 'Add' : 'Remove'}
                </Button>
              ),
            },
          ]}
          rowKey={(record) => record.product}
          size="small"
        />
        <hr />
        <h2 className="title is-size-6">Shipping address</h2>
        <Dragger
          method="get"
          action={async () => '#'} // Don't upload to anywhere
          name="file"
          accept=".csv"
          onRemove={(file) => setCSVs((c) => ({ ...c, [file.uid]: [] }))}
          onChange={onLoadCSV}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Click or drag .csv file to this area
          </p>
        </Dragger>
        {reading && <Loading />}
        <br />
        {address.length > 0 && (
          <Table
            dataSource={address}
            pagination={false}
            columns={[
              {
                title: 'Line 1',
                dataIndex: 'linea1',
                key: 'linea1',
              },
              {
                title: 'Line 2',
                dataIndex: 'linea2',
                key: 'linea2',
              },
              {
                title: 'Line 3',
                dataIndex: 'linea3',
                key: 'linea3',
              },
              {
                title: 'Postal code',
                dataIndex: 'codigo_postal',
                key: 'codigo_postal',
              },
              {
                title: 'Country',
                dataIndex: 'id_pais',
                key: 'id_pais',
              },
              {
                title: 'City',
                dataIndex: 'ciudad',
                key: 'ciudad',
              },
              {
                title: 'Phone',
                dataIndex: 'telefono',
                key: 'telefono',
              },
              {
                title: 'Target',
                dataIndex: 'destinatario',
                key: 'destinatario',
              },
              {
                title: 'Province',
                dataIndex: 'id_provincia',
                key: 'id_provincia',
              },
            ]}
            rowKey={(record) => record.linea1}
            size="small"
          />
        )}
        <hr />
        <h2 className="title is-size-6">Additional order data</h2>
        <label className="label add-order-label">Gift paper</label>
        <Switch onChange={setGift} />
        <label className="label add-order-label">Present message</label>
        <Input.TextArea
          showCount
          maxLength={500}
          onChange={(e) => setPresentMessage(e.target.value)}
        />
      </div>
    </ContentLayout>
  )
}
